import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Loader, Search, ArrowRightLeft, Building2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

export interface MergeCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  deadId: string;
  deadName: string;
  liveReferenceCount: number;
  countsPerTable: Record<string, number>;
  onMergeSuccess: (report: any) => void;
}

/**
 * Escape hatch shown when DELETE /customers/:id returns 409 (live references
 * exist). Lets the user merge the un-deletable client into a survivor — the API
 * repoints every reference to the survivor and soft-deletes the dead id.
 */
export default function MergeCustomerDialog({
  open,
  onClose,
  deadId,
  deadName,
  liveReferenceCount,
  countsPerTable,
  onMergeSuccess,
}: MergeCustomerDialogProps) {
  const [search, setSearch] = useState('');
  const [survivorId, setSurvivorId] = useState('');
  const [error, setError] = useState('');

  // Load candidate survivors. The list endpoint returns active clients only;
  // we still defensively drop any archived/deleted entry and the dead client.
  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.customers ?? []);
    }),
    enabled: open,
  });

  const candidates = useMemo(() => {
    const list = (customers as any[]).filter(
      (c) => c.id !== deadId && !c.deleted_at && !c.deletedAt,
    );
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [customers, deadId, search]);

  const mergeMutation = useMutation({
    mutationFn: () => api.customers.merge(deadId, survivorId),
    onSuccess: (res) => {
      const report = res.data?.data ?? res.data ?? {};
      const repointed = report.total_repointed ?? report.total_live ?? liveReferenceCount;
      toast.success(`Merged — ${repointed} reference${repointed === 1 ? '' : 's'} repointed`);
      onMergeSuccess(report);
      handleClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || 'Failed to merge client');
    },
  });

  function handleClose() {
    setSearch('');
    setSurvivorId('');
    setError('');
    onClose();
  }

  if (!open) return null;

  const countEntries = Object.entries(countsPerTable || {}).filter(([, n]) => (n as number) > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col border border-fieldiq-border">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-fieldiq-border">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={18} className="text-fieldiq-accent flex-shrink-0" />
            <h3 className="text-lg font-bold text-white">
              Can&apos;t delete — {liveReferenceCount} active reference{liveReferenceCount === 1 ? '' : 's'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={mergeMutation.isPending}
            className="text-fieldiq-muted hover:text-white transition-colors flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <p className="text-sm text-fieldiq-text">
            Merge <span className="font-semibold text-white">&ldquo;{deadName}&rdquo;</span> into another
            client to repoint all references before deleting.
          </p>

          {/* Reference counts breakdown */}
          {countEntries.length > 0 && (
            <div className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5">
              <p className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted mb-1.5">
                References
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {countEntries.map(([table, n]) => (
                  <span key={table} className="text-sm text-white">
                    <span className="text-fieldiq-text">{table.replace(/_/g, ' ')}:</span>{' '}
                    <span className="font-semibold">{n as number}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Survivor picker */}
          <div>
            <label className="block text-sm text-white mb-1">Merge into</label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent"
                autoFocus
              />
            </div>

            <div className="border border-fieldiq-border rounded-lg max-h-56 overflow-y-auto divide-y divide-fieldiq-border">
              {isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader size={18} className="animate-spin text-fieldiq-accent" />
                </div>
              )}
              {!isLoading && candidates.length === 0 && (
                <div className="px-3 py-4 text-sm text-fieldiq-muted text-center">
                  No other clients found
                </div>
              )}
              {!isLoading && candidates.map((c: any) => {
                const selected = survivorId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSurvivorId(c.id)}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-2 transition-colors ${
                      selected ? 'bg-fieldiq-accent/15' : 'hover:bg-fieldiq-dark/60'
                    }`}
                  >
                    <div className="w-7 h-7 rounded-md bg-fieldiq-accent/15 flex items-center justify-center flex-shrink-0">
                      <Building2 size={13} className="text-fieldiq-accent" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{c.name}</p>
                      {(c.city || c.provinceState || c.province_state) && (
                        <p className="text-xs text-fieldiq-muted truncate">
                          {[c.city, c.provinceState || c.province_state].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    {selected && <Check size={16} className="text-fieldiq-accent flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-fieldiq-border">
          <button
            onClick={handleClose}
            disabled={mergeMutation.isPending}
            className="px-4 py-2 text-sm text-white hover:text-fieldiq-accent transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => { setError(''); mergeMutation.mutate(); }}
            disabled={!survivorId || mergeMutation.isPending}
            className="flex items-center gap-2 px-5 py-2 bg-fieldiq-accent text-fieldiq-dark font-bold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mergeMutation.isPending && <Loader size={14} className="animate-spin" />}
            {mergeMutation.isPending ? 'Merging...' : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
